import { MisionHogarDb, SCHEMA_VERSION } from '@adapters/dexie/db.ts'
import { DexieEventStore } from '@adapters/dexie/eventStore.ts'
import { DexiePhotoStore } from '@adapters/dexie/photoStore.ts'
import { WebCryptoPinVault } from '@adapters/webcrypto/pinVault.ts'
import { systemClock, systemRandom } from '@adapters/system/clock.ts'
import type { Clock, EventStore, PhotoStore, PinVault, Random } from '@application/ports.ts'

/**
 * The composition root: the one place where the application's ports are bound to
 * real adapters. Everything below this line is testable without a browser.
 */
export interface Runtime {
  readonly db: MisionHogarDb
  readonly store: EventStore
  readonly photos: PhotoStore
  readonly vault: PinVault
  readonly clock: Clock
  readonly random: Random
}

let runtime: Runtime | null = null

export async function getRuntime(): Promise<Runtime> {
  if (runtime) return runtime
  const db = new MisionHogarDb()
  const store = await DexieEventStore.open(db, systemRandom)
  await db.meta.put({ key: 'schemaVersion', value: String(SCHEMA_VERSION) })
  runtime = {
    db,
    store,
    photos: new DexiePhotoStore(db, () => systemRandom.id()),
    vault: new WebCryptoPinVault(db, () => systemClock.now()),
    clock: systemClock,
    random: systemRandom,
  }
  return runtime
}

/** Throws away the open session and its database. Tests only. */
export async function resetRuntimeForTests(): Promise<void> {
  if (runtime) {
    runtime.db.close()
    await runtime.db.delete()
  }
  runtime = null
}

/** Asks the browser not to evict the family's data when disk runs low. */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!('storage' in navigator) || !navigator.storage.persist) return false
  if (await navigator.storage.persisted()) return true
  return navigator.storage.persist()
}

export async function isStoragePersisted(): Promise<boolean> {
  if (!('storage' in navigator) || !navigator.storage.persisted) return false
  return navigator.storage.persisted()
}

/**
 * Whether the app was opened from the home screen rather than a browser tab.
 * Every branch is guarded: a missing `matchMedia` in some embedded WebView must
 * degrade to "we do not know", never crash the render and show a blank tablet.
 *
 * **`display-mode: fullscreen` no vale para esto**, y estaba aquí. Una pestaña
 * normal con el navegador a pantalla completa —en una tablet Android eso pasa
 * continuamente— coincide con esa consulta, así que la app decía «instalada»
 * estando en el navegador. Y decirlo mal no era un detalle: el botón de instalar
 * solo sale cuando cree que no lo está, así que la app se declaraba instalada y
 * escondía justo lo que hacía falta para instalarla.
 *
 * `standalone` sí sirve: ninguna pestaña coincide con él, ni a pantalla completa.
 * Por eso el manifiesto pide `standalone` y ya no `fullscreen` — se pierde el
 * ocultar la barra de estado del sistema, y se gana poder responder la pregunta.
 * Desde dentro de la página no hay manera de distinguir una app instalada a
 * pantalla completa de un navegador a pantalla completa.
 */
export function isInstalled(): boolean {
  const matches = (query: string): boolean => {
    try {
      return window.matchMedia?.(query).matches ?? false
    } catch {
      return false
    }
  }
  return (
    matches('(display-mode: standalone)') ||
    matches('(display-mode: minimal-ui)') ||
    // Safari en iPhone y iPad, que no implementa `display-mode`.
    ('standalone' in navigator && navigator.standalone === true)
  )
}
